import { decodeMutationBatch, encodeMutationBatch } from "@dopejs/pingo-reconciler";
import { MutationTransportBackpressureError } from "./transport-errors";
const TRANSPORT_VERSION = 1;
const MAX_BUFFERED_BYTES = 256 * 1024 * 1024;
/** Bounded, ordered postMessage sender with explicit per-frame acknowledgements. */
export class PostMessageMutationTransport {
    #endpoint;
    #acknowledgementTimeoutMs;
    #maxBufferedBytes;
    #maxBufferedFrames;
    #maxInFlight;
    #onError;
    #sessionId;
    #pending = [];
    #inFlight = [];
    #drainWaiters = [];
    #acknowledged = 0;
    #ackTimer;
    #bufferedBytes = 0;
    #closed = false;
    #failure;
    #highWatermark = 0;
    #latestAcknowledgedSequence = 0;
    #latestAcceptedSequence = 0;
    #merged = 0;
    #rejected = 0;
    #sent = 0;
    #timeouts = 0;
    constructor(endpoint, options) {
        this.#endpoint = endpoint;
        this.#acknowledgementTimeoutMs = boundedInteger(options.acknowledgementTimeoutMs ?? 5_000, 1, 60_000, "acknowledgementTimeoutMs");
        this.#sessionId = positiveU32(options.sessionId, "sessionId");
        this.#maxInFlight = boundedInteger(options.maxInFlight ?? 8, 1, 64, "maxInFlight");
        this.#maxBufferedFrames = boundedInteger(options.maxBufferedFrames ?? 128, this.#maxInFlight, 4096, "maxBufferedFrames");
        this.#maxBufferedBytes = boundedInteger(options.maxBufferedBytes ?? 64 * 1024 * 1024, 4, MAX_BUFFERED_BYTES, "maxBufferedBytes");
        this.#onError = options.onError;
        endpoint.addEventListener("message", this.#handleMessage);
    }
    /** Copies and accepts one complete transaction or throws before changing transport state. */
    enqueue(frameSeq, bytes) {
        this.assertOpen();
        const sequence = positiveU32(frameSeq, "frameSeq");
        if (!(bytes instanceof Uint8Array))
            throw new TypeError("mutation payload must be Uint8Array");
        if (bytes.byteLength === 0 || bytes.byteLength % 4 !== 0) {
            throw new RangeError("mutation payload must be non-empty and four-byte aligned");
        }
        if (this.#latestAcceptedSequence !== 0 &&
            sequence !== nextFrameSequence(this.#latestAcceptedSequence)) {
            throw new Error("mutation frame sequence is not contiguous");
        }
        const bufferedFrames = this.#pending.length + this.#inFlight.length;
        if (bufferedFrames >= this.#maxBufferedFrames ||
            this.#bufferedBytes + bytes.byteLength > this.#maxBufferedBytes) {
            if (this.mergeNewestPending(sequence, bytes))
                return;
            this.#rejected += 1;
            throw new MutationTransportBackpressureError("postMessage mutation buffer is full");
        }
        const copy = bytes.slice().buffer;
        this.#pending.push({ bytes: copy, byteLength: bytes.byteLength, frameSeq: sequence });
        this.#bufferedBytes += bytes.byteLength;
        this.#latestAcceptedSequence = sequence;
        this.#highWatermark = Math.max(this.#highWatermark, bufferedFrames + 1);
        this.flush();
    }
    /** Resolves only after every accepted transaction is acknowledged. */
    drain() {
        if (this.#failure !== undefined)
            return Promise.reject(this.#failure);
        if (this.#pending.length === 0 && this.#inFlight.length === 0)
            return Promise.resolve();
        return new Promise((resolve, reject) => this.#drainWaiters.push({ reject, resolve }));
    }
    /** Stops accepting frames and detaches once already accepted work drains. */
    close() {
        this.#closed = true;
        return this.drain().finally(() => this.detach());
    }
    /** Immediately fails outstanding work, used when a Worker crashes or is replaced. */
    abort(reason = new Error("postMessage mutation transport aborted")) {
        if (this.#failure !== undefined)
            return;
        this.fail(reason);
    }
    metrics() {
        return {
            acknowledged: this.#acknowledged,
            bufferedBytes: this.#bufferedBytes,
            highWatermark: this.#highWatermark,
            inFlight: this.#inFlight.length,
            latestAcknowledgedSequence: this.#latestAcknowledgedSequence,
            latestAcceptedSequence: this.#latestAcceptedSequence,
            merged: this.#merged,
            mode: "post-message",
            queued: this.#pending.length,
            rejected: this.#rejected,
            sent: this.#sent,
            timeouts: this.#timeouts,
        };
    }
    #handleMessage = (event) => {
        const message = event.data;
        if (!isTransportEnvelope(message))
            return;
        if (!isPositiveU32(message.sessionId)) {
            this.fail(new Error("postMessage response has an invalid session"));
            return;
        }
        if (message.sessionId !== this.#sessionId)
            return;
        if (!isTransportMessage(message)) {
            this.fail(new Error("postMessage response is malformed"));
            return;
        }
        if (message.version !== TRANSPORT_VERSION) {
            this.fail(new Error("postMessage transport version mismatch"));
            return;
        }
        if (message.kind === "pingo:mutation-error") {
            this.fail(new Error(`render Worker rejected mutation: ${message.error}`));
            return;
        }
        if (message.kind !== "pingo:mutation-ack")
            return;
        const expected = this.#inFlight[0];
        if (expected === undefined || message.frameSeq !== expected.frameSeq) {
            this.fail(new Error("postMessage acknowledgement is missing, duplicate, or out of order"));
            return;
        }
        this.#inFlight.shift();
        this.clearAcknowledgementTimer();
        this.#bufferedBytes -= expected.byteLength;
        this.#acknowledged += 1;
        this.#latestAcknowledgedSequence = message.frameSeq;
        this.flush();
        this.armAcknowledgementTimer();
        this.resolveDrainIfEmpty();
    };
    flush() {
        while (this.#failure === undefined &&
            this.#inFlight.length < this.#maxInFlight &&
            this.#pending.length > 0) {
            const frame = this.#pending.shift();
            if (frame === undefined)
                return;
            const message = {
                bytes: frame.bytes,
                frameSeq: frame.frameSeq,
                kind: "pingo:mutation",
                sessionId: this.#sessionId,
                version: TRANSPORT_VERSION,
            };
            try {
                this.#endpoint.postMessage(message, [frame.bytes]);
            }
            catch (cause) {
                this.fail(toError(cause, "postMessage mutation send failed"));
                return;
            }
            this.#inFlight.push(frame);
            this.#sent += 1;
        }
        this.armAcknowledgementTimer();
    }
    assertOpen() {
        if (this.#failure !== undefined)
            throw this.#failure;
        if (this.#closed)
            throw new Error("postMessage mutation transport is closed");
    }
    mergeNewestPending(frameSeq, bytes) {
        const previous = this.#pending.at(-1);
        if (previous === undefined)
            return false;
        let merged;
        try {
            const first = decodeMutationBatch(new Uint8Array(previous.bytes));
            const second = decodeMutationBatch(bytes);
            if (first.frameSeq !== previous.frameSeq || second.frameSeq !== frameSeq)
                return false;
            merged = encodeMutationBatch({
                frameSeq,
                mutations: [...first.mutations, ...second.mutations],
            });
        }
        catch {
            return false;
        }
        const nextBufferedBytes = this.#bufferedBytes - previous.byteLength + merged.byteLength;
        if (nextBufferedBytes > this.#maxBufferedBytes)
            return false;
        this.#pending[this.#pending.length - 1] = {
            byteLength: merged.byteLength,
            bytes: merged.slice().buffer,
            frameSeq,
        };
        this.#bufferedBytes = nextBufferedBytes;
        this.#latestAcceptedSequence = frameSeq;
        this.#merged += 1;
        return true;
    }
    fail(error) {
        if (this.#failure !== undefined)
            return;
        this.#failure = error;
        this.#closed = true;
        this.clearAcknowledgementTimer();
        this.detach();
        for (const waiter of this.#drainWaiters.splice(0))
            waiter.reject(error);
        this.#onError?.(error);
    }
    resolveDrainIfEmpty() {
        if (this.#pending.length !== 0 || this.#inFlight.length !== 0)
            return;
        for (const waiter of this.#drainWaiters.splice(0))
            waiter.resolve();
    }
    detach() {
        this.clearAcknowledgementTimer();
        this.#endpoint.removeEventListener("message", this.#handleMessage);
    }
    armAcknowledgementTimer() {
        if (this.#ackTimer !== undefined || this.#failure !== undefined || this.#inFlight.length === 0)
            return;
        this.#ackTimer = globalThis.setTimeout(() => {
            this.#ackTimer = undefined;
            this.#timeouts += 1;
            this.fail(new Error("postMessage mutation acknowledgement timed out"));
        }, this.#acknowledgementTimeoutMs);
    }
    clearAcknowledgementTimer() {
        if (this.#ackTimer === undefined)
            return;
        clearTimeout(this.#ackTimer);
        this.#ackTimer = undefined;
    }
}
/** Worker-side ordered receiver; acknowledgements mean Core and Backend both accepted a frame. */
export class PostMessageMutationReceiver {
    #consume;
    #endpoint;
    #onError;
    #sessionId;
    #disposed = false;
    #lastSequence = 0;
    #processing = Promise.resolve();
    constructor(endpoint, consume, options) {
        this.#endpoint = endpoint;
        this.#consume = consume;
        this.#sessionId = positiveU32(options.sessionId, "sessionId");
        this.#onError = options.onError;
        endpoint.addEventListener("message", this.#handleMessage);
    }
    dispose() {
        if (this.#disposed)
            return;
        this.#disposed = true;
        this.#endpoint.removeEventListener("message", this.#handleMessage);
    }
    #handleMessage = (event) => {
        const message = event.data;
        if (!isMutationEnvelope(message) || this.#disposed)
            return;
        if (!isPositiveU32(message.sessionId)) {
            this.handleFailure(new Error("postMessage mutation has an invalid session"));
            return;
        }
        if (message.sessionId !== this.#sessionId)
            return;
        if (!isMutationMessage(message)) {
            this.handleFailure(new Error("postMessage mutation is malformed"));
            return;
        }
        this.#processing = this.#processing
            .then(() => (this.#disposed ? undefined : this.consume(message)))
            .catch((cause) => this.handleFailure(cause));
    };
    async consume(message) {
        if (message.version !== TRANSPORT_VERSION)
            throw new Error("postMessage transport version mismatch");
        const sequence = positiveU32(message.frameSeq, "frameSeq");
        if (this.#lastSequence !== 0 && !isNewerSequence(sequence, this.#lastSequence)) {
            throw new Error("postMessage mutation sequence is not newer");
        }
        if (!(message.bytes instanceof ArrayBuffer))
            throw new TypeError("mutation bytes must be ArrayBuffer");
        if (message.bytes.byteLength === 0 || message.bytes.byteLength % 4 !== 0) {
            throw new RangeError("mutation bytes must be non-empty and four-byte aligned");
        }
        await this.#consume(sequence, new Uint8Array(message.bytes));
        this.#lastSequence = sequence;
        const acknowledgement = {
            frameSeq: sequence,
            kind: "pingo:mutation-ack",
            sessionId: this.#sessionId,
            version: TRANSPORT_VERSION,
        };
        this.#endpoint.postMessage(acknowledgement);
    }
    postError(error) {
        const message = {
            error: error.message,
            kind: "pingo:mutation-error",
            sessionId: this.#sessionId,
            version: TRANSPORT_VERSION,
        };
        try {
            this.#endpoint.postMessage(message);
        }
        catch {
            // The Worker is already unusable; the main-side error/exit path owns recovery.
        }
    }
    handleFailure(cause) {
        if (this.#disposed)
            return;
        const error = toError(cause, "render Worker mutation failed");
        this.postError(error);
        this.#onError?.(error);
        this.dispose();
    }
}
function isTransportMessage(value) {
    if (!isTransportEnvelope(value))
        return false;
    if (!isPositiveU32(value.sessionId) || !Number.isInteger(value.version))
        return false;
    return value.kind === "pingo:mutation-ack"
        ? isPositiveU32(value.frameSeq)
        : typeof value.error === "string";
}
function isTransportEnvelope(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const kind = value.kind;
    return kind === "pingo:mutation-ack" || kind === "pingo:mutation-error";
}
function isMutationMessage(value) {
    if (!isMutationEnvelope(value))
        return false;
    const message = value;
    return (message.kind === "pingo:mutation" &&
        isPositiveU32(message.sessionId) &&
        Number.isInteger(message.version) &&
        isPositiveU32(message.frameSeq) &&
        message.bytes instanceof ArrayBuffer);
}
function isMutationEnvelope(value) {
    return (typeof value === "object" &&
        value !== null &&
        value.kind === "pingo:mutation");
}
function isPositiveU32(value) {
    return Number.isInteger(value) && value >= 1 && value <= 0xffff_ffff;
}
function nextFrameSequence(value) {
    const next = (value + 1) >>> 0;
    return next === 0 ? 1 : next;
}
function isNewerSequence(candidate, previous) {
    const distance = (candidate - previous) >>> 0;
    return distance !== 0 && distance < 0x8000_0000;
}
function positiveU32(value, label) {
    if (!Number.isInteger(value) || value < 1 || value > 0xffff_ffff) {
        throw new RangeError(`${label} must be a positive u32`);
    }
    return value;
}
function boundedInteger(value, minimum, maximum, label) {
    if (!Number.isInteger(value) || value < minimum || value > maximum) {
        throw new RangeError(`${label} must be an integer from ${String(minimum)} to ${String(maximum)}`);
    }
    return value;
}
function toError(cause, message) {
    return cause instanceof Error ? cause : new Error(message, { cause });
}
//# sourceMappingURL=post-message.js.map